import {
  createContext,
  useReducer,
  useContext,
  FC,
  useCallback,
  useEffect,
  ReactNode
} from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  createCalendarEvent,
  deleteCalendarEvent,
  fetchCalendarEvents,
  updateCalendarEvent
} from 'services/api/calendar';
import {
  LegacyAppointment,
  fetchAppointments
} from 'services/api/appointments';

import {
  State,
  Dispatch,
  DispatchContext,
  CalendarViewType,
  ActiveEventType,
  CalendarState,
  CalendarEventState,
  UpdateCalendarEventType,
  AddCalendarEventType
} from './types';
import reducer from './reducers';
import {
  ActionTypes,
  CALENDAR_EVENT_REPEATING,
  CALENDAR_EVENT_STATUSES,
  CALENDAR_EVENT_TYPES,
  CALENDAR_VIEW
} from './constants';
import { EVENT_COLORS } from 'pages/Calendar/constants';
import { getStatusColor } from 'utils/appointments';
import { showRestError } from 'utils/showErrorMessage';

const initialState: State = {
  view: CALENDAR_VIEW.week,
  events: [],
  appointments: [],
  activeEvent: null,
  dates: null
};

const CalendarStateContext = createContext<CalendarState | undefined>(
  undefined
);

const CalendarEventStateContext = createContext<CalendarEventState | undefined>(
  undefined
);

const CalendarDispatchContext = createContext<Dispatch | undefined>(undefined);

const CalendarProvider: FC<{ children: ReactNode }> = ({ children }) => {
  // context
  const [{ view, events, appointments, activeEvent, dates }, dispatch] =
    useReducer(reducer, {
      ...initialState
    });

  // translations
  const { t } = useTranslation();

  const getEvents = useCallback(
    async (query: { start: Date; end: Date }) => {
      const calendarEvents = await fetchCalendarEvents(query).catch(() => null);

      if (calendarEvents) {
        const eventsData = calendarEvents?.map(item => {
          if (item.type === CALENDAR_EVENT_REPEATING.no_repeat) {
            return {
              ...item,
              id: String(item.id),
              start: item.start || undefined,
              end: item.end || undefined,
              title: item.title || '',
              color: item.color || EVENT_COLORS[0],
              extendedProps: {
                status:
                  item.extendedProps?.status || CALENDAR_EVENT_STATUSES.new,
                description: item.extendedProps?.description || '',
                repeat:
                  item.extendedProps?.repeat ||
                  CALENDAR_EVENT_REPEATING.no_repeat,
                type: CALENDAR_EVENT_TYPES.event
              }
            };
          }

          return {
            id: String(item.id),
            startTime: item.start || undefined,
            endTime: item.end || undefined,
            title: item.title || '',
            color: item.color || EVENT_COLORS[0],
            editable: !!item.extendedProps?.editable,
            startRecur: item.extendedProps?.startRecur,
            daysOfWeek: item.extendedProps?.daysOfWeek,
            extendedProps: {
              status: item.extendedProps?.status || CALENDAR_EVENT_STATUSES.new,
              description: item.extendedProps?.description || '',
              repeat:
                item.extendedProps?.repeat || CALENDAR_EVENT_REPEATING.daily,
              type: CALENDAR_EVENT_TYPES.event
            }
          };
        });

        dispatch({ type: ActionTypes.SET_EVENTS, data: eventsData });
      }
    },
    [dispatch]
  );

  const getAppointments = useCallback(async () => {
    const res = await fetchAppointments({ page: 1, limit: 200 }).catch(
      () => null
    );

    if (!res) return;

    const eventsData = res.results
      .filter((item: LegacyAppointment) =>
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        item.dates?.some((date: any) => date.accepted)
      )
      .map((item: LegacyAppointment) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const startDate = item.dates.find((date: any) => date.accepted);

        const duration = item.plan?.duration || 1;

        const end = dayjs(startDate.date).add(duration, 'hours').format();

        const title = t('calendar.appointment_with', {
          name: `${item.patient?.first_name} ${item.patient?.last_name}`
        });

        return {
          id: String(item.id),
          start: startDate.date,
          end,
          title: title,
          color: getStatusColor(item.status),
          editable: false,
          extendedProps: {
            status: CALENDAR_EVENT_STATUSES.existing,
            repeat: CALENDAR_EVENT_REPEATING.no_repeat,
            type: CALENDAR_EVENT_TYPES.appointment
          }
        };
      });

    dispatch({ type: ActionTypes.SET_APPOINTMENTS, data: eventsData });
  }, [dispatch, t]);

  useEffect(() => {
    if (dates) {
      getEvents(dates);
      getAppointments();
    }
  }, [dates, getAppointments, getEvents]);

  return (
    <CalendarStateContext.Provider
      value={{ view, events, appointments, dates }}
    >
      <CalendarEventStateContext.Provider value={{ activeEvent }}>
        <CalendarDispatchContext.Provider value={dispatch}>
          {children}
        </CalendarDispatchContext.Provider>
      </CalendarEventStateContext.Provider>
    </CalendarStateContext.Provider>
  );
};

const useCalendarStateContext = (): CalendarState => {
  const context = useContext(CalendarStateContext);

  if (typeof context === 'undefined') {
    throw new Error(
      'useCalendarStateContext must be used within a useCalendarStateContext'
    );
  }

  return context;
};

const useCalendarEventStateContext = (): CalendarEventState => {
  const context = useContext(CalendarEventStateContext);

  if (typeof context === 'undefined') {
    throw new Error(
      'useCalendarStateContext must be used within a useCalendarStateContext'
    );
  }

  return context;
};

const useCalendarDispatchContext = (): DispatchContext => {
  const dispatch = useContext(CalendarDispatchContext);

  if (typeof dispatch === 'undefined') {
    throw new Error(
      'useCalendarDispatchContext must be used within a useCalendarDispatchContext'
    );
  }

  const setView = useCallback(
    (value: CalendarViewType) => {
      dispatch({ type: ActionTypes.SET_VIEW, data: value });
    },
    [dispatch]
  );

  const addEvent = useCallback(
    async (event: AddCalendarEventType) => {
      const row = await createCalendarEvent({
        start: event.event.start || event.event.startTime,
        end: event.event.end || event.event.endTime,
        color: event.event.color || EVENT_COLORS[0],
        type:
          event.event.extendedProps?.repeat ||
          CALENDAR_EVENT_REPEATING.no_repeat,
        extendedProps: {
          ...event.event.extendedProps,
          type: CALENDAR_EVENT_TYPES.event,
          editable: event.event.editable,
          startRecur: event.event.startRecur,
          daysOfWeek: event.event.daysOfWeek
        }
      }).catch(err => {
        showRestError(err);

        return null;
      });

      const id = row?.id;

      if (id) {
        dispatch({
          type: ActionTypes.ADD_EVENT,
          data: {
            event: {
              ...event.event,
              id: String(id)
            },
            ui: {
              ...event.ui,
              id: String(id)
            }
          }
        });
      }
    },
    [dispatch]
  );

  const updateEvent = useCallback(
    (event: UpdateCalendarEventType) => {
      updateCalendarEvent(Number(event.id), {
        title: event.title,
        start: event.start || event.startTime,
        end: event.end || event.endTime,
        color: event.color || EVENT_COLORS[0],
        type: event.extendedProps?.repeat,
        extendedProps: {
          ...event.extendedProps,
          type: CALENDAR_EVENT_TYPES.event,
          editable: event.editable,
          startRecur: event.startRecur,
          daysOfWeek: event.daysOfWeek
        }
      }).catch(err => showRestError(err));
      dispatch({ type: ActionTypes.UPDATE_EVENT, data: event });
    },
    [dispatch]
  );

  const setActiveEvent = useCallback(
    (event: ActiveEventType | null) => {
      dispatch({ type: ActionTypes.SET_ACTIVE_EVENT, data: event });
    },
    [dispatch]
  );

  const deleteEvent = useCallback(
    (id: string) => {
      deleteCalendarEvent(Number(id)).catch(err => showRestError(err));
      dispatch({ type: ActionTypes.DELETE_EVENT, data: id });
    },
    [dispatch]
  );

  const setDates = useCallback(
    (start: Date, end: Date) => {
      dispatch({ type: ActionTypes.SET_DATES, data: { start, end } });
    },
    [dispatch]
  );

  return {
    setView,
    addEvent,
    updateEvent,
    setActiveEvent,
    deleteEvent,
    setDates
  };
};

export default CalendarProvider;
export {
  useCalendarDispatchContext,
  useCalendarStateContext,
  useCalendarEventStateContext
};
